import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useCancelShipmentDispatch } from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import { invalidateShipmentLists } from '#/entities/shipment'

/**
 * Cancels the carrier dispatch on an order (the loadboard "Cancel Order"
 * action). Per the spec this reverts the order to POSTED and reposts; the
 * backend owns that transition, so we just invalidate the shipment afterwards.
 */
export function useCancelDispatch(shipmentId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutation = useCancelShipmentDispatch()

  const cancel = () => {
    mutation.mutate(
      { shipmentId, data: {} },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: [`/api/v1/shipments/${shipmentId}`],
          })
          invalidateShipmentLists(queryClient)
          toast.success(t('detailSidebar.loadboard.cancelSuccess'))
        },
        onError: (error) => {
          toast.error(
            getApiErrorDetail(error) ??
              t('detailSidebar.loadboard.cancelError'),
          )
        },
      },
    )
  }

  return { cancel, isPending: mutation.isPending }
}
